import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { StackedBarChartComponent, StackedBarChartData, StackedBarSubgroup, LegendItem } from '../../../../../../_shared/components/graphs/stacked-bar-chart/stacked-bar-chart.component';
import { AuthService, FilterService } from '../../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceCostingService } from '../../../services/maintenance-costing.service';
import { SummaryRequest } from '../../../../../../_shared/models/summary-request.model';
import { hasCompleteDateRange, splitWidgetFilter } from '../../../../../../_shared/helpers/widget-filter.helper';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { MaintenanceInsightsResponse } from '../../../../maintenance-dashboard/models/maintenance-dashboard-response.model';
import { combineKpiInsights } from '../../../helpers/kpi-insights.helper';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { TimeSeriesGroupingHelper } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { HighestCostByContractTypeModalComponent } from '../../modals/highest-cost-by-contract-type-modal/highest-cost-by-contract-type-modal.component';
import { Subject } from 'rxjs';
import { WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { formatValue } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-highest-cost-by-contract-type',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, StackedBarChartComponent, EmptyStateComponent],
  templateUrl: './highest-cost-by-contract-type.component.html',
  styleUrl: './highest-cost-by-contract-type.component.scss'
})
export class HighestCostByContractTypeComponent implements OnInit, OnDestroy {
  private modalService = inject(NgbModal);
  private costingService = inject(MaintenanceCostingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);
  private authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);

  chartData: StackedBarChartData[] = [];
  readonly chartHeight = 145;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly subgroups: StackedBarSubgroup[] = [
    { name: 'fmc', legend: 'FMC', color: '#E6B04D' },
    { name: 'fml', legend: 'FML', color: '#BACA62' },
    { name: 'serviceOnly', legend: this.transloco.translate('maintenanceCosting.charts.highestCostByContractType.legend.serviceOnly'), color: '#66AECF' }
  ];

  readonly legendItems: LegendItem[] = [
    { legend: 'FMC', color: '#E6B04D' },
    { legend: 'FML', color: '#BACA62' },
    { legend: this.transloco.translate('maintenanceCosting.charts.highestCostByContractType.legend.serviceOnly'), color: '#66AECF' }
  ];

  get title(): string {
    return this.transloco.translate('maintenanceCosting.charts.highestCostByContractType.title');
  }

  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';
  currencyCode: string = '';

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    this.filterService.filterUpdated$
      .pipe(takeUntilDestroyed())
      .subscribe(async (filter) => {
        if (!hasCompleteDateRange(filter)) return;

        this.widgetFilter = filter;
        await this.loadContractTypeData();
      });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    // The shared filter may still hold another page's selection
    if (hasCompleteDateRange(this.widgetFilter)) {
      this.loadContractTypeData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadContractTypeData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: SummaryRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const [fmcResponse, fmlResponse, serviceOnlyResponse] = await Promise.all([
        this.costingService.CostContractByTypeFMC(request, this.cancelSubject),
        this.costingService.CostContractByTypeFML(request, this.cancelSubject),
        this.costingService.CostContractByTypeServiceOnly(request, this.cancelSubject)
      ]);

      this.chartData = this.processContractTypeData(
        fmcResponse.data?.data ?? [],
        fmlResponse.data?.data ?? [],
        serviceOnlyResponse.data?.data ?? []
      );
      this.yTicks = this.calculateYTicks(this.chartData);

      const summary = combineKpiInsights([fmcResponse.data, fmlResponse.data, serviceOnlyResponse.data]);
      this.totalValue = summary.totalValue;
      this.percentageChange = summary.percentageChange;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.chartData = [];
          this.isLoading = false;
        }
      });
    }
  }

  private processContractTypeData(
    fmcData: MaintenanceInsightsResponse[],
    fmlData: MaintenanceInsightsResponse[],
    serviceOnlyData: MaintenanceInsightsResponse[]
  ): StackedBarChartData[] {
    if (fmcData.length === 0 && fmlData.length === 0 && serviceOnlyData.length === 0) return [];

    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const datasets = [
      { label: 'fmc', data: fmcData },
      { label: 'fml', data: fmlData },
      { label: 'serviceOnly', data: serviceOnlyData }
    ];

    let groupedData: any[];

    switch (groupingType) {
      case 'hourly':
        groupedData = TimeSeriesGroupingHelper.groupByHours(datasets);
        break;
      case 'daily':
        groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
        break;
      default:
        groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }

    // Transform grouped data to StackedBarChartData format
    return groupedData.map(item => ({
      label: item.label,
      dateRange: item.dateRange,
      fmc: item.fmc ?? 0,
      fml: item.fml ?? 0,
      serviceOnly: item.serviceOnly ?? 0
    }));
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(chartData: StackedBarChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    // For stacked bars, we need to calculate the sum of all segments
    const stackedTotals = chartData.map(item => {
      return (Number(item['fmc']) || 0) +
        (Number(item['fml']) || 0) +
        (Number(item['serviceOnly']) || 0);
    });

    return calculateChartYTicks(stackedTotals, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false; // Cost increase is negative
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      // Check all subgroups for non-zero values
      return this.subgroups.some(subgroup => {
        const value = Number(item[subgroup.name]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    const modalRef = this.modalService.open(HighestCostByContractTypeModalComponent, modalOptions);
    Object.assign(modalRef.componentInstance, splitWidgetFilter(this.filterService.filter));
  }
}